import { useCallback, useEffect, useState } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { APP_VERSION, releaseToShow, type Release } from '../lib/changelog'

const SEEN_KEY = 'gs:last-seen-version'
const UPDATE_CHECK_MS = 60 * 60 * 1000

function readSeen(): string | null {
  try {
    return localStorage.getItem(SEEN_KEY)
  } catch {
    return null
  }
}

function writeSeen(version: string) {
  try {
    localStorage.setItem(SEEN_KEY, version)
  } catch {
    /* private mode / storage disabled — just skip the announcement next time */
  }
}

/**
 * Opt-in updates: a new service worker waits until the user accepts it, and the
 * release it brought is announced once on the load after that. Both only to
 * someone signed in: the landing and the login page never show them, and a
 * visit there doesn't use up the announcement. The worker registers and
 * checks for updates for every visitor.
 */
export function useAppUpdate(signedIn: boolean) {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    // Browsers only check for a new worker on navigation; an installed PWA can
    // stay open for days, so poll hourly too.
    onRegisteredSW(_url, registration) {
      if (registration) setInterval(() => void registration.update(), UPDATE_CHECK_MS)
    },
  })

  // "Later" only hides the banner for this session — it comes back on next load
  // while the new worker is still waiting.
  const [dismissed, setDismissed] = useState(false)

  // Decided once, the first time someone is signed in, and the version recorded
  // then, so the sheet shows up exactly once per release.
  const [whatsNew, setWhatsNew] = useState<Release | null>(null)
  const [decided, setDecided] = useState(false)
  if (signedIn && !decided) {
    setDecided(true)
    setWhatsNew(releaseToShow(APP_VERSION, readSeen()))
  }
  useEffect(() => { if (decided) writeSeen(APP_VERSION) }, [decided])

  const update = useCallback(() => {
    void updateServiceWorker(true)
  }, [updateServiceWorker])

  return {
    updateAvailable: needRefresh && !dismissed && signedIn,
    update,
    dismiss: useCallback(() => setDismissed(true), []),
    whatsNew: signedIn ? whatsNew : null,
    dismissWhatsNew: useCallback(() => setWhatsNew(null), []),
  }
}
